import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Input, Select, Space, Table, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { API } from "../../../../helpers/API/amplifyApi";
import LoadableComp from "../../../commonComponents/LoadableComp/LoadableComp";
import MondayButton from "../../../commonComponents/MondayButton/MondayButton";
import { programFields as programFieldsAction } from "../../../../store/actions";
import {
  APP_DEFAULT_TZ,
  COMMON_TIMEZONES,
  EMPLOYEE_TEAM_FIELD_ID,
  findEmployeeTeamField,
  normalizeTeamOptions,
} from "../../../../utils/timezone/teamTimezone";
import "./TeamTimezoneSettings.scss";

const FIELD_NAME = "Employee Team";

async function loadTeamField() {
  try {
    const existing = await API.get(
      "programFields",
      `/programFields/${EMPLOYEE_TEAM_FIELD_ID}`
    );
    if (existing?.fieldId) return existing;
  } catch {
    /* create below */
  }
  return API.post("programFields", "/programFields", {
    body: {
      fieldId: EMPLOYEE_TEAM_FIELD_ID,
      fieldName: FIELD_NAME,
      fieldOptions: [
        { name: "Flex Tirana", timezone: "Europe/Tirane" },
        { name: "Flex Prishtina", timezone: "Europe/Belgrade" },
      ],
    },
  });
}

function TeamTimezoneSettings() {
  const dispatch = useDispatch();
  const { isDarkMode } = useSelector((state) => state.usersList);
  const programFieldsList = useSelector(
    (state) => state.programFields?.programFields
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);

  const syncRedux = useCallback(
    (fieldOptions) => {
      const list = Array.isArray(programFieldsList)
        ? [...programFieldsList]
        : [];
      const idx = list.findIndex((f) => f?.fieldId === EMPLOYEE_TEAM_FIELD_ID);
      const row = {
        fieldId: EMPLOYEE_TEAM_FIELD_ID,
        fieldName: FIELD_NAME,
        fieldOptions,
      };
      if (idx >= 0) list[idx] = { ...list[idx], ...row };
      else list.push(row);
      dispatch(programFieldsAction(list));
    },
    [dispatch, programFieldsList]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fromRedux = findEmployeeTeamField(programFieldsList);
      if (fromRedux?.fieldOptions) {
        setRows(normalizeTeamOptions(fromRedux.fieldOptions));
      }
      const field = await loadTeamField();
      const next = normalizeTeamOptions(field?.fieldOptions);
      setRows(next);
      syncRedux(next);
    } catch (err) {
      console.error(err);
      message.error("Nuk u ngarkua dot lista e ekipeve.");
    } finally {
      setLoading(false);
    }
  }, [programFieldsList, syncRedux]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    const cleaned = normalizeTeamOptions(rows);
    if (cleaned.some((r) => !r.name || !r.timezone)) {
      message.error("Çdo ekip duhet të ketë emër dhe timezone.");
      return;
    }
    const names = cleaned.map((r) => r.name.toLowerCase());
    if (new Set(names).size !== names.length) {
      message.error("Emrat e ekipeve duhet të jenë unikë.");
      return;
    }
    setSaving(true);
    try {
      await loadTeamField();
      await API.put(
        "programFields",
        `/programFields/${EMPLOYEE_TEAM_FIELD_ID}`,
        { body: { fieldOptions: cleaned } }
      );
      setRows(cleaned);
      syncRedux(cleaned);
      message.success("Timezone-et e ekipeve u ruajtën.");
    } catch (err) {
      console.error(err);
      message.error("Ndodhi një gabim gjatë ruajtjes.");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Ekipi",
      dataIndex: "name",
      key: "name",
      render: (value, record, index) => (
        <Input
          value={value}
          placeholder="p.sh. Flex Tirana"
          onChange={(e) => {
            const next = [...rows];
            next[index] = { ...record, name: e.target.value };
            setRows(next);
          }}
        />
      ),
    },
    {
      title: "Timezone (IANA)",
      dataIndex: "timezone",
      key: "timezone",
      width: 320,
      render: (value, record, index) => (
        <Select
          showSearch
          style={{ width: "100%" }}
          value={value || APP_DEFAULT_TZ}
          options={COMMON_TIMEZONES}
          optionFilterProp="label"
          onChange={(tz) => {
            const next = [...rows];
            next[index] = { ...record, timezone: tz };
            setRows(next);
          }}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      width: 64,
      render: (_, __, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
        />
      ),
    },
  ];

  if (loading) return <LoadableComp />;

  return (
    <div
      className={`team-tz-settings ${isDarkMode ? "dark" : ""}`}
    >
      <div className="team-tz-settings__head">
        <h2>Timezone e ekipeve</h2>
        <p>
          Çdo ekip ka timezone IANA. DST trajtohet automatikisht (p.sh.
          Europe/Tirane, Europe/Belgrade për Prishtinë). Ekipet e reja nuk janë
          të kufizuara — zgjidhni çdo timezone nga lista.
        </p>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Parazgjedhjet"
        description="Flex Tirana → Europe/Tirane · Flex Prishtina → Europe/Belgrade (Prishtina). Fallback kompani: Europe/Tirane."
      />

      <Space style={{ marginBottom: 12 }}>
        <Button
          icon={<PlusOutlined />}
          onClick={() =>
            setRows((prev) => [
              ...prev,
              { name: "", timezone: APP_DEFAULT_TZ },
            ])
          }
        >
          Shto ekip
        </Button>
        <MondayButton
          className="mondayButtonGreen"
          onClick={save}
          spin={saving}
        >
          Ruaj
        </MondayButton>
      </Space>

      <Table
        rowKey={(_, i) => `team-${i}`}
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="middle"
      />
    </div>
  );
}

export default TeamTimezoneSettings;
